import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import {
  AllergiesPopUp,
  ErrorAlert,
  PageLoadingSpinner,
  PrescriptionPopUp,
  useAdjustPageHeadEntities,
} from 'ui_kit_qmd'
import { UpdateStatusPopUp } from './components/update-status-popup/UpdateStatusPopUp'
import { transformStatus } from './Utils'
import errorIcon from '../../images/common/error-icon.svg'
import {
  ARE_YOU_SURE,
  CANCEL_TEXT,
  CONFIRM_TEXT,
  ON_HOLD_CONFIRMATION_POP_UP_BODY_TEXT,
  ON_HOLD_CONFIRMATION_POP_UP_HEADER,
  ON_HOLD_CONFIRMATION_POP_UP_PRIMARY_BTN_TEXT,
  ON_HOLD_CONFIRMATION_POP_UP_SECONDARY_BTN_TEXT,
  PATIENT_CHARTS_PAGE_ROUTE,
  PATIENT_CHARTS_TEXT,
  PATIENT_TEXT,
  QUICK_MD_TITLE_TEXT,
  REMOVE_ALLERGIES_CONFIRMATION,
  profilePermissions,
} from '../../utils/constants'
import { PatientChartHeader } from './components/patient-chart-header/PatientChartHeader'
import {
  addAllergy,
  deleteAllergy,
  getPatientInfo,
  setOnHoldStatus,
  setPatientHasAllergies,
  setPatientHasNoAllergies,
  setOnHoldStatusWithReason,
} from '../../store/charts/ChartsThunk'
import {
  setChartsError,
  setIsGeneralInfoUpdatedSuccess,
  setIsPatientInfoLoadedSuccess,
  setPatientStatusChangedSuccess,
} from '../../store/charts/ChartsActions'
import { AccountInformationSection } from './components/account-information-section/AccountInformationSection'
import { AlertsSection } from './components/alerts-section/AlertsSection'
import { GeneralInformationSection } from './components/general-information-section/GeneralInformationSection'
import { PastVisitsSection } from './components/past-visits-section/PastVisitsSection'
import { PrescriptionsSection } from './components/prescriptions-section/PrescriptionsSection'
import { ChartListener } from '../../store/charts/ChartListener'
import { FilesSection } from './components/files-section/FilesSection'
import { ConfirmationPopup } from '../../components/confirmation-popup/confirmationpopup'
import { errorCodes, getUserFriendlyErrorMessage } from '../../utils/errormessages'
import './PatientChartPage.scss'
import { IntakeForms } from './components/intake-forms/intakeForms'
import { UpcomingVisitsSection } from './components/upcoming-visits-section/UpcomingVisitsSection'
import { generateLoggPatientChartAccessData, postHIPAALogMessage } from '../../utils/HippaLoggingHelper'
import { AuditLogs } from './components/audit-log/AuditLogs'
import { getTabId, parseTimeZone } from '../../utils/common'
import { text } from './Constants'
import { formatNameForPatientChartTitle } from './helper'
import { setDrugRulesSettingsByUserId } from '../../store/drugRulesSettings/drugRulesSettingsActions'
import { CancellationPopUp } from './components/cancelation-pop-up/CancellationPopUp'
import { ChangeToSentPopUp } from './components/change-to-sent-pop-up/ChangeToSentPopUp'
import { DuplicateAccountsSection } from './components/DuplicateAccountsSection'

const TREAT_SYNC_ERROR_TIMEOUT_MS = 7000

const errorToastContainerStyle = {
  width: 550,
  height: 'auto',
  minHeight: 56,
  background: '#fdf5f2',
  zIndex: 10,
}

// @ts-expect-error TS6133 -- MIGRATION: not approved, fix when encountered
const pageContainerStyle = {
  overflow: 'hidden',
}
const removeAllAllergiesPopUpStyle = { marginTop: 0, alignSelf: 'center', width: 717, alignItems: 'center' }

export const PatientChartPage = memo(() => {
  const [params] = useSearchParams()
  const id = params.get('id')
  const { state: locationState } = useLocation()
  const [isPopUpOpen, setIsPopUpOpen] = useState(false)

  const [onHoldReason, setOnHoldReason] = useState('')

  const onClosePopUp = useCallback(() => setIsPopUpOpen(false), [])

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const alertSectionRef = useRef()
  const pastVisitSectionRef = useRef()
  const upcomingVisitSectionRef = useRef()
  const filesSectionRef = useRef()
  const tabId = useRef(getTabId())
  const allergiesPopUpRef = useRef()
  // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
  const handleConfirm = (status, reason) => {
    // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
    dispatch(setOnHoldStatusWithReason(id, status, reason))
  }

  // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
  const error = useSelector((state) => state.chartsReducer.error)
  // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
  const loading = useSelector((state) => state.chartsReducer.loading)
  // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
  const phoneNumberValidationLoading = useSelector((state) => state.member.loading)
  // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
  const patientInfo = useSelector((state) => state.chartsReducer.patientInfo)
  // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
  const isPatientInfoLoadedSuccess = useSelector((state) => state.chartsReducer.isPatientInfoLoadedSuccess)
  // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
  const isGeneralInfoUpdatedSuccess = useSelector((state) => state.chartsReducer.isGeneralInfoUpdatedSuccess)
  // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
  const profile = useSelector((state) => state.accountSettings.adminProfile)
  // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
  const settingsPerState = useSelector((state) => state.drugRulesSettingsReducer.settingsPerState)
  // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
  const isPatientStatusChangedSuccess = useSelector((state) => state.chartsReducer.isPatientStatusChangedSuccess)
  const [selectedHoldStatus, setSelectedHoldStatus] = useState(transformStatus(patientInfo?.[id]?.RestrictionStatus))

  const [isGeneralInfoEditMode, setIsGeneralInfoEditMode] = useState(false)
  const [isOpenOnHoldConfirmationPopUp, setIsOpenOnHoldConfirmationPopUp] = useState(false)
  const [isSideCollapsed, setIsSideCollapsed] = useState(false)
  const [isPatientInfoDownloaded, setIsPatientInfoDownloaded] = useState(false)
  const [isUserOnHold, setIsUserOnHold] = useState(null)
  const [_isFirstRender, setIsFirstRender] = useState(true)
  const [removeAllAllergiesPopUp, setRemoveAllAllergiesPopUp] = useState(false)
  const [allergyErrorValue, setAllergyErrorValue] = useState('')
  const [isOpenAllergiesPopUp, setIsOpenAllergiesPopUp] = useState(false)
  const [prescription, setPrescription] = useState(null)
  const [cancelOrderId, setCancelOrderId] = useState('')
  const [sentOrderId, setSentOrderId] = useState('')
  const [treatSyncError, setTreatSyncError] = useState('')

  const sideBarRef = useRef()
  const [userTimeZone, setUserTimeZone] = useState(parseTimeZone(profile.TimeZone))

  useEffect(() => {
    if (sideBarRef) {
      // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
      const sideBarState = sideBarRef.current?.isSideBarCollapsed()
      setIsSideCollapsed(sideBarState)
    }
  }, [sideBarRef])
  useEffect(() => {
    if (patientInfo?.[id]?.RestrictionStatus) {
      setSelectedHoldStatus(transformStatus(patientInfo?.[id]?.RestrictionStatus))
    }
  }, [patientInfo, id])

  const { FirstName, LastName } = patientInfo?.[id] || {}
  const pageTitle = `${formatNameForPatientChartTitle(FirstName, LastName)} - Patient Chart`
  const pageHeader = useAdjustPageHeadEntities({
    title: pageTitle,
  })
  useEffect(
    () => () => {
      document.title = QUICK_MD_TITLE_TEXT
    },
    [],
  )

  // @ts-expect-error TS6133 -- MIGRATION: not approved, fix when encountered
  const onCollapseSidePanel = (newStatus) => {
    setTimeout(() => {
      setIsSideCollapsed(newStatus)
    }, 200)
  }

  const currentPatientInfo = patientInfo?.[id]
  const isMergedAccount = currentPatientInfo?.RestrictionStatus === 'merged'

  useEffect(() => {
    setUserTimeZone(parseTimeZone(profile.TimeZone))
  }, [profile])
  useEffect(() => {
    if (allergyErrorValue) {
      setAllergyErrorValue('')
    }
  }, [allergyErrorValue])
  useEffect(() => {
    if (error) {
      if (isPopUpOpen) {
        onClosePopUp()
      }
    }
  }, [error, isPopUpOpen])

  useEffect(() => {
    if (isPatientStatusChangedSuccess) {
      const timer = setTimeout(() => {
        dispatch(setPatientStatusChangedSuccess(false))
        onClosePopUp()
      }, 3500)

      return () => clearTimeout(timer)
    }
  }, [isPatientStatusChangedSuccess, dispatch, onClosePopUp])

  useEffect(() => {
    if (id) {
      // @ts-expect-error TS2554 -- MIGRATION: not approved, fix when encountered
      dispatch(getPatientInfo(id))
      postHIPAALogMessage(
        generateLoggPatientChartAccessData({
          userId: profile?.AdminId || '',
          patientId: id,
        }),
      )
    } else {
      navigate('/not-found')
    }
  }, [id])

  useEffect(() => {
    dispatch(
      setDrugRulesSettingsByUserId({
        patientHomeAddressState: patientInfo?.[id]?.Address?.StateCode,
        patientId: id,
      }),
    )
  }, [dispatch, id, patientInfo])

  useEffect(() => {
    if (settingsPerState && patientInfo?.[id]?.Address?.StateCode) {
      dispatch(
        setDrugRulesSettingsByUserId({
          patientId: id,
          patientHomeAddressState: patientInfo?.[id]?.Address?.StateCode,
        }),
      )
    }
  }, [patientInfo, settingsPerState])

  useEffect(() => {
    if (isPatientInfoDownloaded) {
      setIsUserOnHold(
        // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
        patientInfo?.[id]?.RestrictionStatus !== 'none' && patientInfo?.[id]?.RestrictionStatus !== 'Compliant',
      )
    }
  }, [id, isPatientInfoDownloaded, patientInfo])

  const onCloseOnHoldConfirmationPopUp = useCallback(() => {
    setIsOpenOnHoldConfirmationPopUp(false)
  }, [])

  useEffect(() => {
    onCloseOnHoldConfirmationPopUp()
  }, [isUserOnHold, onCloseOnHoldConfirmationPopUp])

  useEffect(() => {
    // @ts-expect-error TS7034 -- MIGRATION: not approved, fix when encountered
    let timer
    if (currentPatientInfo?.TreatSyncError) {
      setTreatSyncError(
        `Patient's Treat profile could not be created. Please correct error to enable Treat prescribing. Error: ${currentPatientInfo?.TreatSyncError}`,
      )
      timer = setTimeout(() => {
        setTreatSyncError('')
      }, TREAT_SYNC_ERROR_TIMEOUT_MS)
    }
    return () => {
      // @ts-expect-error TS7005 -- MIGRATION: not approved, fix when encountered
      if (timer) clearTimeout(timer)
    }
  }, [currentPatientInfo?.TreatSyncError])

  useEffect(() => {
    if (error) {
      if (error === getUserFriendlyErrorMessage(errorCodes.NOT_FOUND, null, PATIENT_TEXT)) {
        // @ts-expect-error TS2554 -- MIGRATION: not approved, fix when encountered
        dispatch(setChartsError())
        navigate('/not-found')
        return
      }

      if (error === getUserFriendlyErrorMessage(errorCodes.CANNOT_SAVE_NEGATIVE_BALANCE)) {
        // @ts-expect-error TS2554 -- MIGRATION: not approved, fix when encountered
        dispatch(getPatientInfo(id))
      }
      if (
        error === getUserFriendlyErrorMessage(errorCodes.ALLERGY_NAME_NOT_UNIQUE) ||
        error === getUserFriendlyErrorMessage(errorCodes.ONLY_NUMBERS_AND_LETTERS_ALLOWED)
      ) {
        setAllergyErrorValue(error)
        dispatch(setChartsError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS)))
      }

      setTimeout(() => {
        // @ts-expect-error TS2554 -- MIGRATION: not approved, fix when encountered
        dispatch(setChartsError())
      }, 3500)
    }
  }, [dispatch, error])

  useEffect(() => {
    if (isGeneralInfoUpdatedSuccess) {
      // @ts-expect-error TS2554 -- MIGRATION: not approved, fix when encountered
      dispatch(setIsGeneralInfoUpdatedSuccess())
      // @ts-expect-error TS2554 -- MIGRATION: not approved, fix when encountered
      dispatch(getPatientInfo(id))
    }
  }, [isGeneralInfoUpdatedSuccess])

  useEffect(() => {
    if (isPatientInfoLoadedSuccess) {
      if (!isPatientInfoDownloaded) {
        // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
        setIsUserOnHold(patientInfo?.[id]?.RestrictionStatus !== 'none')
        setIsPatientInfoDownloaded(true)
        setIsFirstRender(false)
      }
      // @ts-expect-error TS2554 -- MIGRATION: not approved, fix when encountered
      dispatch(setIsPatientInfoLoadedSuccess())
    }
  }, [isPatientInfoLoadedSuccess])

  // @ts-expect-error TS6133 -- MIGRATION: not approved, fix when encountered
  const crumbsConfig = useMemo(
    () => [
      {
        site: PATIENT_CHARTS_TEXT,
        location: `${PATIENT_CHARTS_PAGE_ROUTE}${locationState?.search ?? ''}`,
      },
      {
        site: `${currentPatientInfo?.FirstName || ''} ${currentPatientInfo?.LastName || ''}`,
        location: '',
      },
    ],
    [currentPatientInfo?.FirstName, currentPatientInfo?.LastName, locationState?.search],
  )

  const onCloseAlertSection = useCallback(() => {
    // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
    alertSectionRef.current?.closeAlertForm()
  }, [])
  const onOpenPastVisits = useCallback(() => {
    // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
    pastVisitSectionRef.current?.openPastVisits()
  }, [])
  const onOpenUpcomingVisits = useCallback(() => {
    // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
    upcomingVisitSectionRef.current?.openUpcomingVisits()
  }, [])
  const onOpenFiles = useCallback(() => {
    // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
    filesSectionRef.current?.openFiles()
  }, [])
  const onCloseTagsPopUp = useCallback(() => {
    // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
    filesSectionRef.current?.closeFileTags()
  }, [])
  const onCloseUpload = useCallback(() => {
    // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
    filesSectionRef.current?.closeUpload()
  }, [])

  // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
  const setToastErrorMessage = (message) => {
    dispatch(setChartsError(message))
  }

  const onClickContinueBtn = useCallback(() => {
    // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
    dispatch(setOnHoldStatus(id, !isUserOnHold))
  }, [dispatch, id, isUserOnHold])

  const errorMsg =
    error === getUserFriendlyErrorMessage(errorCodes.CANNOT_SAVE_NEGATIVE_BALANCE) ?
      getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS)
    : error

  const openAllergiesPopUp = useCallback(() => {
    setIsOpenAllergiesPopUp(true)
  }, [])

  const onCloseAllergiesPopup = useCallback(() => {
    setIsOpenAllergiesPopUp(false)
  }, [])

  const handleConfirmNoAllergies = useCallback(() => {
    setRemoveAllAllergiesPopUp(false)
    // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
    dispatch(setPatientHasNoAllergies(currentPatientInfo.PatientId, tabId.current))
  }, [dispatch, currentPatientInfo?.PatientId])

  const handleNoAllergiesClick = useCallback(() => {
    if (currentPatientInfo?.AllergyCollection?.HasNoAllergyConfirmation) {
      // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
      dispatch(setPatientHasAllergies(currentPatientInfo?.PatientId, tabId.current))
    } else if (currentPatientInfo?.AllergyCollection?.Allergies.length) {
      setRemoveAllAllergiesPopUp(true)
    } else {
      // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
      dispatch(setPatientHasNoAllergies(currentPatientInfo.PatientId, tabId.current))
    }
  }, [
    currentPatientInfo?.AllergyCollection?.HasNoAllergyConfirmation,
    currentPatientInfo?.AllergyCollection?.Allergies.length,
    currentPatientInfo?.PatientId,
    dispatch,
  ])
  const setChartErrorAction = useCallback(() => {
    dispatch(setChartsError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS)))
  }, [dispatch])

  const onAddAllergy = useCallback(
    // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
    (allergy) => {
      // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
      dispatch(addAllergy(currentPatientInfo?.PatientId, allergy, tabId.current))
    },
    [dispatch, currentPatientInfo?.PatientId],
  )

  const onDeleteAllergy = useCallback(
    // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
    (allergyId) => {
      // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
      dispatch(deleteAllergy(currentPatientInfo?.PatientId, allergyId, tabId.current))
    },
    [dispatch, currentPatientInfo?.PatientId],
  )

  const clearForm = useCallback(() => {
    // @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
    allergiesPopUpRef?.current.clearForm()
  }, [])

  const handleRemoveAllAllergiesPopUp = useCallback(() => {
    setRemoveAllAllergiesPopUp(false)
  }, [])

  return (
    <>
      {prescription ?
        <PrescriptionPopUp
          prescription={prescription}
          onClose={() => {
            setPrescription(null)
          }}
          // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
          onChangeToCancel={(orderId) => {
            setCancelOrderId(orderId)
            setPrescription(null)
          }}
          onChangeToSent={
            profile?.Permissions?.includes(profilePermissions.MANAGE_PRESCRIPTION_STATUS) ?
              // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
              (orderId) => {
                setSentOrderId(orderId)
                setPrescription(null)
              }
            : undefined
          }
        />
      : null}
      {cancelOrderId ?
        <CancellationPopUp
          orderId={cancelOrderId}
          // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
          patientId={id}
          onClosePopUp={() => {
            setCancelOrderId('')
          }}
        />
      : null}
      {sentOrderId ?
        <ChangeToSentPopUp
          orderId={sentOrderId}
          // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
          patientId={id}
          onClosePopUp={() => {
            setSentOrderId('')
          }}
        />
      : null}
      {pageHeader}
      <ChartListener
        patientId={id}
        closeAlertSection={onCloseAlertSection}
        openPastVisits={onOpenPastVisits}
        openUpcomingVisits={onOpenUpcomingVisits}
        openFiles={onOpenFiles}
        closeTagsPopUp={onCloseTagsPopUp}
        closeUpload={onCloseUpload}
        tabId={tabId.current}
        onClearForm={clearForm}
      />
      {removeAllAllergiesPopUp ?
        // @ts-expect-error TS2739 -- MIGRATION: not approved, fix when encountered
        <ConfirmationPopup
          primaryBtnAction={handleConfirmNoAllergies}
          title={ARE_YOU_SURE}
          msg={REMOVE_ALLERGIES_CONFIRMATION}
          primaryBtnText={CONFIRM_TEXT}
          secondaryBtnAction={handleRemoveAllAllergiesPopUp}
          secondaryBtnText={CANCEL_TEXT}
          popUpBoxStyle={removeAllAllergiesPopUpStyle}
          closeAction={handleRemoveAllAllergiesPopUp}
          className='patient-chart-allergy-confirmation-popup'
        />
      : null}

      {isPopUpOpen ?
        <UpdateStatusPopUp
          popUpTitle='On hold status'
          popUpBodyText="Changing the compliance status will limit the user's booking options, requiring an in-person visit or drug screen."
          selectedStatus={selectedHoldStatus}
          setSelectedStatus={setSelectedHoldStatus}
          onHoldReason={onHoldReason}
          setOnHoldReason={setOnHoldReason}
          // Popup open/close logic
          isPatientStatusChangedSuccess={isPatientStatusChangedSuccess}
          isOpen={isPopUpOpen}
          onClose={onClosePopUp}
          onConfirm={handleConfirm}
        />
      : null}

      {isOpenAllergiesPopUp ?
        <AllergiesPopUp
          ref={allergiesPopUpRef}
          closePopUpCallback={onCloseAllergiesPopup}
          allergiesList={currentPatientInfo?.AllergyCollection?.Allergies || []}
          hasNoAllergies={currentPatientInfo?.AllergyCollection?.HasNoAllergyConfirmation}
          toggleNoAllergies={handleNoAllergiesClick}
          position='fixed'
          localError={allergyErrorValue}
          setError={setChartErrorAction}
          addPatientAllergy={onAddAllergy}
          deletePatientAllergy={onDeleteAllergy}
          getAllergies={() => {}}
          fieldRequiredAllergyError={text.FIELD_REQUIRED_ALLERGY_ERROR}
          fieldRequiredSeverityError={text.FIELD_REQUIRED_SEVERITY_ERROR}
          uniqueError={text.ALLERGY_UNIQUE_ERROR}
          validationError={text.ALLERGY_VALIDATION_ERROR}
          shouldShow={!removeAllAllergiesPopUp}
        />
      : null}
      {isOpenOnHoldConfirmationPopUp ?
        // @ts-expect-error TS2739 -- MIGRATION: not approved, fix when encountered
        <ConfirmationPopup
          title={ON_HOLD_CONFIRMATION_POP_UP_HEADER}
          className='on-hold-confirmation-pop-up-fixed-position'
          primaryBtnAction={onClickContinueBtn}
          primaryBtnText={ON_HOLD_CONFIRMATION_POP_UP_PRIMARY_BTN_TEXT}
          popUpBoxStyle={{}}
          secondaryBtnAction={onCloseOnHoldConfirmationPopUp}
          secondaryBtnText={ON_HOLD_CONFIRMATION_POP_UP_SECONDARY_BTN_TEXT}
          msg={ON_HOLD_CONFIRMATION_POP_UP_BODY_TEXT}
          closeAction={onCloseOnHoldConfirmationPopUp}
        />
      : null}
      {loading || phoneNumberValidationLoading ?
        <PageLoadingSpinner />
      : null}
      {error || treatSyncError ?
        <ErrorAlert
          styleContainer={errorToastContainerStyle}
          errorText={errorMsg || treatSyncError}
          errorImg={errorIcon}
          imgAlt='Error'
        />
      : null}
      <div className='patient-chart-page-main-wrapper'>
        <PatientChartHeader
          // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
          setIsOpenOnHoldConfirmationPopUp={setIsOpenOnHoldConfirmationPopUp}
          isOnHoldStatus={isUserOnHold}
          setIsPopUpOpen={setIsPopUpOpen}
          BACKEND_TO_UI_STATUS
          selectedHoldStatus={transformStatus(patientInfo?.[id]?.RestrictionStatus)}
          onOpenAllergiesPopUp={openAllergiesPopUp}
        />
        <div className='patient-chart-page-body'>
          <DuplicateAccountsSection />

          <div className='patient-chart-page-body-content-container'>
            <AlertsSection
              ref={alertSectionRef}
              // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
              id={id}
              isMergedAccount={isMergedAccount}
              isSideCollapsed={isSideCollapsed}
              userTimeZone={userTimeZone}
            />

            <AccountInformationSection
              // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
              isMergedAccount={isMergedAccount}
              isUserOnHold={isUserOnHold}
              setIsGeneralInfoEditMode={setIsGeneralInfoEditMode}
              userTimeZone={userTimeZone}
            />

            <GeneralInformationSection
              // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
              isGeneralInfoEditMode={isGeneralInfoEditMode}
              isMergedAccount={isMergedAccount}
              userTimeZone={userTimeZone}
            />

            {/*
             // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered */}
            <PrescriptionsSection userTimeZone={userTimeZone} onSetPrescription={setPrescription} />

            <PastVisitsSection
              // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
              id={id}
              patientInfo={patientInfo}
              ref={pastVisitSectionRef}
              userTimeZone={userTimeZone}
              patientChartsSearchRow={locationState?.search}
            />

            <UpcomingVisitsSection
              // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
              id={id}
              patientInfo={patientInfo}
              ref={upcomingVisitSectionRef}
              userTimeZone={userTimeZone}
              patientChartsSearchRow={locationState?.search}
            />

            <IntakeForms id={id} userTimeZone={userTimeZone} patientChartsSearchRow={locationState?.search} />

            <FilesSection
              // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
              id={id}
              isMergedAccount={isMergedAccount}
              patientInfo={patientInfo}
              ref={filesSectionRef}
              userTimeZone={userTimeZone}
              setToastErrorMessage={setToastErrorMessage}
            />

            {/*
             // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered */}
            <AuditLogs id={id} userTimeZone={userTimeZone} />
          </div>
        </div>
      </div>
    </>
  )
})
